import { bottomLevelId, sortedLevels, topLevelId } from "building-generation/elements/level/levelTools";
import type { Building } from "building-types/building/buildingTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { PlanarGraphLevelAssociations } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { PlanarGraphPerimeterInfo } from "building-types/planarGraphs/planarGraphPerimeterInfo";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomEnclosure } from "building-types/rooms/roomTypesEnum";
import type { StructureSettings } from "building-types/settings/structureSettings";
import type { RWTable, Table } from "common/data/table";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { closestPointPolyLine2 } from "geom/closestPoint";
import {
  classifyContainmentPolyLine2PolyLine2,
  classifyContainmentPolyLine2PolyLine2Fast,
  classifyContainmentVec2PolyLine2,
  PolyLine2Containment,
  PolyLine2PolyLine2ContainmentClass,
  Vec2PolyLine2ContainmentClass,
} from "geom/containment";
import { DIST_EPSILON } from "geom/core";
import { CurveCurveRangeIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import { line2Collinear } from "geom/line2";
import {
  planarGraphHalfEdgeEnd,
  planarGraphHalfEdgeNormal,
  planarGraphHalfEdgeStripToPolyLine2,
  planarGraphHalfEdgeTangent,
  planarGraphHalfEdgeToLine2Mut,
  planarGraphTracePerimeter,
  planarGraphVertexStripToPolyLine2,
} from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2Simplify } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { range } from "geom/utils";
import { vec2Dot } from "geom/vec2";
import { getPerimeterStreetOrientations } from "../elements/streetOrientation";
import { getInsideCornerHalfEdges } from "../elements/walls/wallInsideCorners";

export function getPlanarGraphPerimeterInfo(
  levelRooms: string[],
  levelPolygonPlanarGraph: PolygonPlanarGraph,
  rooms: Table<Room>,
  roomLevelIndices: Table<number>,
  mirror: boolean,
): PlanarGraphPerimeterInfo {
  const levelPlanarGraph = levelPolygonPlanarGraph.planarGraph;

  const allPerimeterFaces = range(levelPlanarGraph.faceVertices.length);
  const allPerimeters = planarGraphTracePerimeter(levelPlanarGraph);
  const allPerimeterStreetOrientations = getPerimeterStreetOrientations(allPerimeters, levelPolygonPlanarGraph, mirror);

  const uncoveredPerimeterFaces = getPlanarGraphFacesForRoomEnclosureIsNone(
    rooms,
    roomLevelIndices,
    levelRooms,
    levelPolygonPlanarGraph,
  );

  // excludes just porches from perimeter trace
  const uncoveredPerimeters = planarGraphTracePerimeter(levelPlanarGraph, uncoveredPerimeterFaces);

  const uncoveredPerimeterStreetOrientations = getPerimeterStreetOrientations(
    uncoveredPerimeters,
    levelPolygonPlanarGraph,
    mirror,
  );

  const coveredPerimeterFaces = getExteriorPlanarGraphFaces(
    rooms,
    roomLevelIndices,
    levelRooms,
    levelPolygonPlanarGraph,
  );

  // excludes all exterior spaces (porches, freestanding fireplaces) from perimeter trace
  const coveredPerimeters =
    coveredPerimeterFaces.length > 0
      ? planarGraphTracePerimeter(levelPlanarGraph, coveredPerimeterFaces)
      : uncoveredPerimeters;

  const coveredPerimeterStreetOrientations = getPerimeterStreetOrientations(
    coveredPerimeters,
    levelPolygonPlanarGraph,
    mirror,
  );

  const insideCornerHalfEdges = getInsideCornerHalfEdges({
    planarGraph: levelPlanarGraph,
    coveredPerimeters,
  });

  return {
    allPerimeters,
    allPerimeterFaces,
    allPerimeterStreetOrientations,
    uncoveredPerimeters,
    uncoveredPerimeterFaces,
    uncoveredPerimeterStreetOrientations,
    coveredPerimeters,
    coveredPerimeterFaces,
    coveredPerimeterStreetOrientations,
    unsupportedPerimeterVertices: [], // generated downstream
    insideCornerHalfEdges,
  };
}

/** Assumes that all faces are covered */
export function getRoofPlanarGraphPerimeterInfo(
  planarGraph: PolygonPlanarGraph,
  mirror: boolean,
): PlanarGraphPerimeterInfo {
  const allPerimeterFaces = range(planarGraph.planarGraph.faceVertices.length);
  const allPerimeters = planarGraphTracePerimeter(planarGraph.planarGraph);
  const allPerimeterStreetOrientations = getPerimeterStreetOrientations(allPerimeters, planarGraph, mirror);

  return {
    allPerimeterFaces,
    allPerimeters,
    allPerimeterStreetOrientations,
    uncoveredPerimeterFaces: [],
    uncoveredPerimeters: { faceVertices: [], faceHalfEdges: [] },
    uncoveredPerimeterStreetOrientations: {},
    coveredPerimeterFaces: allPerimeterFaces,
    coveredPerimeters: allPerimeters,
    coveredPerimeterStreetOrientations: allPerimeterStreetOrientations,
    insideCornerHalfEdges: [],
    unsupportedPerimeterVertices: [],
  };
}

export function getPlanarGraphFaces(
  rooms: Table<Room>,
  roomLevelIndices: Table<number>,
  roomsOnLevel: string[],
  graph: PolygonPlanarGraph,
  predicate?: (room: Room) => boolean | undefined,
): number[] {
  const faceIds: number[] = [];
  for (const id of roomsOnLevel) {
    const room = rooms[id];
    if (predicate === undefined || predicate(room)) {
      // defensive lookup due to crashes observed in bugsnag
      const index = graph.polygonToPlanarGraph[roomLevelIndices[id]]?.[0]?.[0]?.index;
      if (index !== undefined) {
        faceIds.push(index);
      }
    }
  }
  return faceIds;
}

function getExteriorPlanarGraphFaces(
  rooms: Table<Room>,
  roomLevelIndices: Table<number>,
  roomsOnLevel: string[],
  graph: PolygonPlanarGraph,
) {
  return getPlanarGraphFaces(
    rooms,
    roomLevelIndices,
    roomsOnLevel,
    graph,
    (room) => !isWalledRoomEnclosure[room.attributes.roomEnclosure],
  );
}

function getPlanarGraphFacesForRoomEnclosureIsNone(
  rooms: Table<Room>,
  roomLevelIndices: Table<number>,
  roomsOnLevel: string[],
  graph: PolygonPlanarGraph,
) {
  return getPlanarGraphFaces(
    rooms,
    roomLevelIndices,
    roomsOnLevel,
    graph,
    (room) => room.attributes.roomEnclosure === RoomEnclosure.NONE,
  );
}

export function getPlanarGraphFacesForRoomEnclosureIsNotNone(
  rooms: Table<Room>,
  roomLevelIndices: Table<number>,
  roomsOnLevel: string[],
  graph: PolygonPlanarGraph,
) {
  return getPlanarGraphFaces(
    rooms,
    roomLevelIndices,
    roomsOnLevel,
    graph,
    (room) => room.attributes.roomEnclosure !== RoomEnclosure.NONE,
  );
}

enum CornerType {
  NONE,
  CONVEX,
  CONCAVE,
}

export function addPlanarGraphUnsupportedPerimeterVerticesToPerimeterInfo(
  levels: Table<Level>,
  planarGraphs: Table<PolygonPlanarGraph>,
  planarGraphPerimeters: Table<PlanarGraphPerimeterInfo>,
  structureSettings: StructureSettings,
) {
  const maxUnsupportedCornerSpan = structureSettings?.maxUnsupportedCornerSpan;

  const top = topLevelId(levels);
  const bottom = bottomLevelId(levels);

  if (!top || !bottom) {
    return;
  }

  // loop backwards through floors supported by other floors
  for (let levelId = top; levelId !== bottom; levelId = levels[levelId]?.levelBelow ?? bottom) {
    const unsupportedPerimeterVertices: number[] = [];

    const level = levels[levelId];
    if (!level) {
      break;
    }
    const levelBelowId = level.levelBelow;

    if (!levelBelowId) {
      continue;
    }

    // level below
    const lowerPolygonPlanarGraph = planarGraphs[levelBelowId];
    if (!lowerPolygonPlanarGraph) {
      continue;
    }
    const lowerPlanarGraph = lowerPolygonPlanarGraph.planarGraph;
    const { coveredPerimeters: lowerCoveredPerimeters } = planarGraphPerimeters[levelBelowId];

    if (maxUnsupportedCornerSpan === undefined || lowerCoveredPerimeters.faceVertices.length === 0) {
      planarGraphPerimeters[levelBelowId].unsupportedPerimeterVertices = unsupportedPerimeterVertices;
      continue;
    }

    const lowerCoveredPolyLine2s: PolyLine2[] = [];
    for (const face of lowerCoveredPerimeters.faceVertices) {
      let epl = planarGraphVertexStripToPolyLine2(lowerPlanarGraph, face);
      epl = polyLine2Simplify(epl);
      lowerCoveredPolyLine2s.push(epl);
    }

    // level above
    const upperPolygonPlanarGraph = planarGraphs[levelId];
    if (!upperPolygonPlanarGraph) {
      planarGraphPerimeters[levelBelowId].unsupportedPerimeterVertices = unsupportedPerimeterVertices;
      continue;
    }
    const upperPlanarGraph = upperPolygonPlanarGraph.planarGraph;
    const { coveredPerimeters: upperCoveredPerimeters } = planarGraphPerimeters[levelId];

    if (lowerCoveredPolyLine2s.length === 0) {
      planarGraphPerimeters[levelBelowId].unsupportedPerimeterVertices = unsupportedPerimeterVertices;
      continue;
    }

    for (let i = 0, l = upperCoveredPerimeters.faceVertices.length; i < l; i++) {
      const boundaryFaceHalfEdges = upperCoveredPerimeters.faceHalfEdges[i];
      const boundaryFaceHalfEdgesCount = boundaryFaceHalfEdges.length;

      for (let j = 0, jLen = boundaryFaceHalfEdgesCount; j < jLen; j++) {
        const currJ = j;
        const nextJ = (currJ + 1) % boundaryFaceHalfEdgesCount;

        const halfEdge = boundaryFaceHalfEdges[currJ];
        const nextHalfEdge = boundaryFaceHalfEdges[nextJ];

        const [edgeNormalX, edgeNormalY] = planarGraphHalfEdgeNormal(upperPlanarGraph, halfEdge);
        const [nextEdgeDirX, nextEdgeDirY] = planarGraphHalfEdgeTangent(upperPlanarGraph, nextHalfEdge);

        let corner = CornerType.NONE;
        const endDot = vec2Dot(edgeNormalX, edgeNormalY, nextEdgeDirX, nextEdgeDirY);

        if (endDot <= -DIST_EPSILON) {
          corner = CornerType.CONCAVE;
        } else if (endDot >= DIST_EPSILON) {
          corner = CornerType.CONVEX;
        }

        if (corner === CornerType.NONE) {
          continue;
        }

        const [cornerX, cornerY] = planarGraphHalfEdgeEnd(upperPlanarGraph, halfEdge);

        // currently does not deal with unsupported edges in holes in the lower floor, but we don't have any in our homes
        const containment = classifyContainmentVec2PolyLine2(cornerX, cornerY, lowerCoveredPolyLine2s[0]);
        if (containment !== Vec2PolyLine2ContainmentClass.OUTSIDE) {
          continue;
        }

        const cp = closestPointPolyLine2(lowerCoveredPolyLine2s[0], cornerX, cornerY);
        if (cp && cp.dist > maxUnsupportedCornerSpan) {
          unsupportedPerimeterVertices.push(upperPlanarGraph.halfEdges[halfEdge * 2 + 1]);
        }
      }
    }

    planarGraphPerimeters[levelBelowId].unsupportedPerimeterVertices = unsupportedPerimeterVertices;
  }
}

export interface PlanarGraphAssociations {
  /**
   * These store previous graph half-edges that are spatiallyassociated with each current graph half-edge.
   * Indexed by current graph half-edge index, contains arrays of previous graph half-edge indices.
   */
  prevHalfEdges: number[][];
  /**
   * These store current graph half-edges that are spatially associated with each previous graph half-edge.
   * Indexed by previous graph half-edge index, contains arrays of current graph half-edge indices.
   */
  currHalfEdges: number[][];
  /**
   * These store previous graph faces that are spatially associated with each current graph face.
   * Indexed by current graph face index, contains arrays of previous graph face indices.
   */
  prevFaces: number[][];
  /**
   * These store current graph faces that are spatially associated with each previous graph face.
   * Indexed by previous graph face index, contains arrays of current graph face indices.
   */
  currFaces: number[][];
}

export function computePlanarGraphAssociations(
  currGraph: PolygonPlanarGraph,
  prevGraph: PolygonPlanarGraph,
  currGraphFacePls: { [faceIndex: number]: PolyLine2 },
  prevGraphFacePls: { [faceIndex: number]: PolyLine2 },
  heEdge: PolyLine2,
  oheEdge: PolyLine2,
): PlanarGraphAssociations {
  const prevHalfEdges: number[][] = [];
  const currHalfEdges: number[][] = [];
  const prevFaces: number[][] = [];
  const currFaces: number[][] = [];

  const prevGraphVisitedFaces: { [currFaceIndex: number]: { [prevFaceIndex: number]: true } } = {};

  for (let j = 0, k = prevGraph.planarGraph.halfEdges.length; j < k; j += 2) {
    const ohe = j / 2;
    currHalfEdges[ohe] = [];
  }

  for (let i = 0, l = currGraph.planarGraph.halfEdges.length; i < l; i += 2) {
    const he = i / 2;
    planarGraphHalfEdgeToLine2Mut(currGraph.planarGraph, he, heEdge);
    const heFace = currGraph.planarGraph.halfEdgeParentFace[he];

    prevHalfEdges[he] = [];
    prevFaces[heFace] = prevFaces[heFace] ?? [];
    prevGraphVisitedFaces[heFace] = prevGraphVisitedFaces[heFace] ?? {};

    currGraphFacePls[heFace] =
      currGraphFacePls[heFace] ??
      planarGraphHalfEdgeStripToPolyLine2(currGraph.planarGraph, currGraph.planarGraph.faceHalfEdges[heFace]);

    for (let j = 0, k = prevGraph.planarGraph.halfEdges.length; j < k; j += 2) {
      const ohe = j / 2;
      planarGraphHalfEdgeToLine2Mut(prevGraph.planarGraph, ohe, oheEdge);
      const oheFace = prevGraph.planarGraph.halfEdgeParentFace[ohe];

      if (!prevGraphVisitedFaces[heFace][oheFace]) {
        prevGraphVisitedFaces[heFace][oheFace] = true;

        const hePl = currGraphFacePls[heFace];
        prevGraphFacePls[oheFace] =
          prevGraphFacePls[oheFace] ??
          planarGraphHalfEdgeStripToPolyLine2(prevGraph.planarGraph, prevGraph.planarGraph.faceHalfEdges[oheFace]);
        const ohePl = prevGraphFacePls[oheFace];

        const containment = classifyContainmentPolyLine2PolyLine2(hePl, ohePl);
        const reverseContainmentFast = classifyContainmentPolyLine2PolyLine2Fast(ohePl, hePl);

        if (
          containment !== PolyLine2PolyLine2ContainmentClass.OUTSIDE &&
          containment !== PolyLine2PolyLine2ContainmentClass.OUTSIDE_COINCIDENT
        ) {
          prevFaces[heFace].push(oheFace);
          if (!currFaces[oheFace]) {
            currFaces[oheFace] = [];
          }
          currFaces[oheFace].push(heFace);
        } else if (reverseContainmentFast === PolyLine2Containment.INSIDE) {
          prevFaces[heFace].push(oheFace);
          if (!currFaces[oheFace]) {
            currFaces[oheFace] = [];
          }
          currFaces[oheFace].push(heFace);
        }
      }

      const collinear = line2Collinear(
        heEdge[0],
        heEdge[1],
        heEdge[2],
        heEdge[3],
        oheEdge[0],
        oheEdge[1],
        oheEdge[2],
        oheEdge[3],
      );

      if (!collinear) {
        continue;
      }

      const intersect = intersectLine2Line2(
        heEdge[0],
        heEdge[1],
        heEdge[2],
        heEdge[3],
        oheEdge[0],
        oheEdge[1],
        oheEdge[2],
        oheEdge[3],
        DIST_EPSILON,
      );

      if (intersect instanceof CurveCurveRangeIntersection) {
        prevHalfEdges[he].push(ohe);
        if (!currHalfEdges[ohe]) {
          currHalfEdges[ohe] = [];
        }
        currHalfEdges[ohe].push(he);
      }
    }
  }

  return {
    prevHalfEdges,
    currHalfEdges,
    prevFaces,
    currFaces,
  };
}

export function makePlanarGraphLevelAssociations(levels: Table<Level>, planarGraphs: Building["planarGraphs"]) {
  const planarGraphHalfEdgesBelow: RWTable<number[][]> = {};
  const planarGraphHalfEdgesAbove: RWTable<number[][]> = {};

  const planarGraphFacesBelow: RWTable<number[][]> = {};
  const planarGraphFacesAbove: RWTable<number[][]> = {};

  const planarGraphLevelAssociations: RWTable<PlanarGraphLevelAssociations> = {};

  // instead of allocating a new polyLine2 for each half edge, we can reuse the same ones
  // using planarGraphHalfEdgeToLine2Mut
  const heEdge = polyLine2New(4);
  const oheEdge = polyLine2New(4);

  const graphFacePls: RWTable<{ [faceIndex: number]: PolyLine2 }> = {};

  const orderedLevels = sortedLevels(levels);
  for (const currLevel of orderedLevels) {
    const prevLevel = currLevel.levelBelow ? levels[currLevel.levelBelow] : undefined;
    const prevGraphFacePls = prevLevel ? graphFacePls[prevLevel.id] : {};

    planarGraphHalfEdgesBelow[currLevel.id] = [];
    planarGraphFacesBelow[currLevel.id] = [];

    const currGraph = planarGraphs[currLevel.id];

    if (!graphFacePls[currLevel.id]) {
      graphFacePls[currLevel.id] = {};
    }

    const currGraphFacePls = graphFacePls[currLevel.id];

    if (!prevLevel) {
      planarGraphLevelAssociations[currLevel.id] = {
        halfEdgesBelow: planarGraphHalfEdgesBelow[currLevel.id],
        halfEdgesAbove: [],
        facesBelow: planarGraphFacesBelow[currLevel.id],
        facesAbove: [],
      };
      continue;
    }

    const prevGraph = planarGraphs[prevLevel.id];

    const associations = computePlanarGraphAssociations(
      currGraph,
      prevGraph,
      currGraphFacePls,
      prevGraphFacePls,
      heEdge,
      oheEdge,
    );

    planarGraphHalfEdgesBelow[currLevel.id] = associations.prevHalfEdges;
    planarGraphFacesBelow[currLevel.id] = associations.prevFaces;

    planarGraphHalfEdgesAbove[prevLevel.id] = associations.currHalfEdges;
    planarGraphFacesAbove[prevLevel.id] = associations.currFaces;

    planarGraphLevelAssociations[currLevel.id] = {
      halfEdgesBelow: planarGraphHalfEdgesBelow[currLevel.id],
      halfEdgesAbove: [],
      facesBelow: planarGraphFacesBelow[currLevel.id],
      facesAbove: [],
    };

    planarGraphLevelAssociations[prevLevel.id] = {
      ...planarGraphLevelAssociations[prevLevel.id],
      halfEdgesAbove: planarGraphHalfEdgesAbove[prevLevel.id],
      facesAbove: planarGraphFacesAbove[prevLevel.id],
    };
  }

  return planarGraphLevelAssociations;
}
